/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useContext } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Cell } from "react-table";
import { Box, Text } from "@nordcloud/gnui";
import { FlexContainer, Table } from "~/components";
import { CloudAccountAdd } from "~/components/Applications/ApplicationDetails/CloudAccountAdd";
import { CloudAccountRemove } from "~/components/Applications/ApplicationDetails/CloudAccountRemove";
import { CloudAccount } from "~/models";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";

function CloudAccounts() {
  const { application, updateApplication, canEdit } = useContext(
    ApplicationViewCtx
  );
  const accounts = application.cloudAccounts;

  const addAccount = React.useCallback(
    (newAccount: CloudAccount) =>
      updateApplication({ cloudAccounts: [...accounts, newAccount] }),
    [accounts, updateApplication]
  );

  const removeAccount = React.useCallback(
    (idx: number) =>
      updateApplication({
        cloudAccounts: accounts.filter((_, i) => i !== idx),
      }),
    [accounts, updateApplication]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Cloud Provider",
        accessor: "cloudProvider",
      },
      {
        Header: "Account ID",
        accessor: "accountId",
      },
      {
        Header: "Account Name",
        accessor: "accountName",
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: Cell<CloudAccount>) => {
          return (
            canEdit && (
              <FlexContainer>
                <CloudAccountRemove
                  removeCallback={removeAccount}
                  account={row?.original}
                  idx={row.index}
                />
              </FlexContainer>
            )
          );
        },
      },
    ],
    [canEdit, removeAccount]
  );

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={12}>
          <FlexContainer>
            <Text weight="medium">Cloud Accounts</Text>
            <CloudAccountAdd addAccount={addAccount} />
          </FlexContainer>
          <Table columns={columns} data={accounts} mb="0" />
        </Col>
      </Row>
    </Box>
  );
}

export { CloudAccounts };
